import {
  getAuthUrl,
  getForumsRegisterUrl,
  getForumsSignInUrl,
  getForumsSignOutUrl,
  toAbsoluteCommunityUrl,
} from 'utils/url-generator';
import { CommunityApp, CommunityNavigation, LoggedInUser } from '@atlassian/learning-components';
import { ENVIRONMENT, getEnvironmentAsEnum } from 'utils/environment';
import {
  CommunityAnalyticsClient,
  getCommunityLoginUrl,
  getCommunityLogoutUrl,
  eventType,
} from '@atlassian/community-library';
import {
  accountSwitcherIsActive,
  hlvVanillaMigrationIsActive,
  hlvVanillaPrereleaseUrlIsActive,
  newNavigationIsActive,
} from '../../services/statsig/featureGates';
import { getAppByRoute } from '../../utils/route-utils';

interface Props {
  isLoggedIn: boolean;
  loggedInUser: LoggedInUser;
}

export const TopNavigationContents = ({ isLoggedIn, loggedInUser }: Props) => {
  // using globalThis.location causes a hydration error since the location will be a
  // tesseract server when it renders on the server instead of community-ui.atlassian.com
  const pathname = globalThis.location.pathname;

  const currentPage = `${process.env.HOME_BASE_URL}${pathname.replace('index.html', '')}`;
  const encodedCurrentPage = encodeURIComponent(currentPage);
  const currentEnv = getEnvironmentAsEnum();
  const useHlvVanilla = hlvVanillaMigrationIsActive();
  const loginHref = getLoginHref(currentPage, encodedCurrentPage, loggedInUser.email || '', useHlvVanilla, currentEnv);
  const logoutHref = getLogoutHref(
    currentPage,
    encodedCurrentPage,
    loggedInUser.email || '',
    useHlvVanilla,
    currentEnv,
  );
  const signupHref = getSignupHref(encodedCurrentPage, loggedInUser.email || '', useHlvVanilla);
  const switchHref = getSwitchHref(currentPage, encodedCurrentPage, useHlvVanilla);
  const appButtonHrefOverrides = getAppButtonHrefOverrides(useHlvVanilla);

  return (
    <CommunityNavigation
      currentApp={getAppByRoute(pathname)}
      isLoggedIn={isLoggedIn}
      user={loggedInUser}
      loginHref={loginHref}
      logoutHref={logoutHref}
      signupHref={signupHref}
      switchHref={switchHref}
      appButtonHrefOverrides={appButtonHrefOverrides}
      onAnalyticsSendEvent={(type, payload) =>
        CommunityAnalyticsClient.sendEvent(type as unknown as eventType, payload)
      }
      email={loggedInUser.email}
      showNewMenu={newNavigationIsActive()}
    />
  );
};

const getHlvVanillaPathPrefix = (): '/forums' | '/new-hlv-forums' =>
  hlvVanillaPrereleaseUrlIsActive() ? '/new-hlv-forums' : '/forums';

const getHlvVanillaForumsBaseUrl = (): string => `${process.env.HOME_BASE_URL}${getHlvVanillaPathPrefix()}`;

const getAppButtonHrefOverrides = (useHlvVanilla: boolean) => {
  if (!useHlvVanilla) {
    return undefined;
  }

  const forumsBaseUrl = getHlvVanillaForumsBaseUrl();

  return {
    [CommunityApp.FORUMS]: {
      loggedIn: forumsBaseUrl,
      loggedOut: forumsBaseUrl,
    },
  };
};

const getLoginHref = (
  currentPage: string,
  encodedCurrentPage: string,
  email: string,
  useHlvVanilla: boolean,
  env: ENVIRONMENT,
) => {
  if (useHlvVanilla) {
    // Top-nav sign-in bootstraps forums and lands on the selected HLV forums home.
    const forumsHomeUrl = `${getHlvVanillaForumsBaseUrl()}/`;
    const forumsSignInUrl = toAbsoluteCommunityUrl(getForumsSignInUrl({ redirectUrl: forumsHomeUrl }));
    return getAuthUrl('login', {
      redirectUrl: encodeURIComponent(forumsSignInUrl),
      experience: 'entry-auth',
      email,
    });
  }

  return getCommunityLoginUrl(
    {
      redirectUrl: encodedCurrentPage,
      experience: 'entry-auth',
      email,
    },
    env,
  );
};

const getLogoutHref = (
  currentPage: string,
  encodedCurrentPage: string,
  email: string,
  useHlvVanilla: boolean,
  env: ENVIRONMENT,
) => {
  if (useHlvVanilla) {
    const forumSignOutUrl = toAbsoluteCommunityUrl(
      `${getForumsSignOutUrl(process.env.KHOROS_ENDPOINT_PATH || '')}?Target=${encodeURIComponent(currentPage)}`,
    );
    return getAuthUrl('logout', {
      redirectUrl: encodeURIComponent(forumSignOutUrl),
      experience: 'entry-auth',
      email,
    });
  }

  return getCommunityLogoutUrl(
    {
      redirectUrl: encodedCurrentPage,
      experience: 'entry-auth',
      email,
    },
    env,
  );
};

const getSignupHref = (encodedCurrentPage: string, email: string, useHlvVanilla: boolean) => {
  if (useHlvVanilla) {
    const forumsHomeUrl = getHlvVanillaForumsBaseUrl();
    const forumsRegisterUrl = toAbsoluteCommunityUrl(getForumsRegisterUrl({ redirectUrl: forumsHomeUrl }));
    return getAuthUrl('signup', {
      redirectUrl: encodeURIComponent(forumsRegisterUrl),
      experience: 'entry-auth',
      email,
    });
  }

  return getAuthUrl('signup', {
    redirectUrl: encodedCurrentPage,
    experience: 'entry-auth',
    email,
  });
};

const getSwitchHref = (_currentPage: string, encodedCurrentPage: string, _useHlvVanilla: boolean) => {
  return accountSwitcherIsActive()
    ? getAuthUrl('login', {
        redirectUrl: encodedCurrentPage,
        experience: 'entry-auth',
        prompt: 'select_account',
      })
    : '';
};
